import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { CheckCircle2, Circle } from 'lucide-react';
import type { PanelId } from '../RentalWizardPage';
import { useWizardStore } from '../wizard-store';

/**
 * M1.1 — Véglegesítés-előtti ellenőrző-lista a step4-ben.
 *
 * A 4 HARD-required panel (`partner`/`equipment`/`schedule`/`payment`) live
 * `getPanelValidity(id).isValid` állapotát vizualizálja — ugyanaz a 4 feltétel,
 * amit az `isSubmittable()` aggregál (SSoT, NEM újraszámolt logika). Így a
 * pultos a Kiadás ELŐTT látja mi hiányzik, és a lista sosem csúszhat el a
 * tényleges submit-gate-től (measured-gate: a UI a valós gate-et tükrözi).
 */
const CHECKLIST_ITEMS: ReadonlyArray<{ id: PanelId; labelKey: string; step: 1 | 2 | 3 | 4 }> = [
  { id: 'partner', labelKey: 'rental.finalizeChecklist.itemPartner', step: 1 },
  { id: 'equipment', labelKey: 'rental.finalizeChecklist.itemEquipment', step: 2 },
  { id: 'schedule', labelKey: 'rental.finalizeChecklist.itemSchedule', step: 3 },
  { id: 'payment', labelKey: 'rental.finalizeChecklist.itemPayment', step: 4 },
];

export function FinalizeChecklist() {
  const { t } = useTranslation();
  const { getPanelValidity, gotoStep } = useWizardStore();

  const items = CHECKLIST_ITEMS.map(item => ({
    ...item,
    isValid: getPanelValidity(item.id).isValid,
  }));
  const missingCount = items.filter(i => !i.isValid).length;
  const allReady = missingCount === 0;

  return (
    <section
      aria-label={t('rental.finalizeChecklist.title')}
      className="rounded-lg border border-gray-200 bg-white p-4 dark:border-[#2d7d8a] dark:bg-transparent"
    >
      <p
        className={cn(
          'mb-3 text-sm font-semibold',
          allReady ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'
        )}
      >
        {allReady
          ? t('rental.finalizeChecklist.allReady')
          : t('rental.finalizeChecklist.incomplete', { count: missingCount })}
      </p>
      <ul className="space-y-2">
        {items.map(item => (
          <li key={item.id} className="flex items-center gap-2 text-sm">
            {item.isValid ? (
              <CheckCircle2
                className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400"
                aria-label={t('rental.finalizeChecklist.doneAria')}
              />
            ) : (
              <Circle
                className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500"
                aria-label={t('rental.finalizeChecklist.missingAria')}
              />
            )}
            <span
              className={cn(item.isValid ? 'text-foreground' : 'text-gray-500 dark:text-[#ccfbf1]')}
            >
              {t(item.labelKey)}
            </span>
            {!item.isValid && (
              <button
                type="button"
                onClick={() => gotoStep(item.step)}
                className="ml-auto shrink-0 text-xs font-semibold text-primary hover:underline"
              >
                {t('rental.finalizeChecklist.fix')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
